/* lib/units.js — pure unit-derivation helpers, ported VERBATIM from the
 * prototype (LHC_Catalog_Technician_trimmed HTML 53802-53831, 54694-54696,
 * 56474-56475, 56574-56584). No data dependencies — everything is derived
 * from the unit record itself (SKU / dims / cat heuristics).
 *
 * Preserved quirks (do NOT "fix"):
 *   • unitWidthCm reads the canonical dims string first (2026-08-24 bridge
 *     parity), falling back to the first purely-numeric SKU segment.
 *   • unitHasFront: SKU config segment (2nd hyphen part) not starting "op".
 *   • dims values >= 320 are treated as mm and divided by 10 (mm tolerance).
 *   • unitGroup returns "kitchens" in both regex branches (prototype 54694). */

export const PLINTH_CATS = ["Base Unit", "Corner Base", "Drawer Unit", "Tall Unit", "Column"]; // base-family + tall-family floor units

export function unitNeedsPlinth(u) { return !!u && PLINTH_CATS.indexOf(u.cat) >= 0; }

/** skuWidthCm(u) — the SKU numeric segment ALONE (e.g. BU-D1S1-30-L → 30cm).
 *  2026-07-26 canonical-fidelity fix: a segment may carry a trailing variant
 *  suffix ("15 L", "15L" from "BU-D1S1-15 L-R") — the LEADING number is the
 *  width; letter-first segments ("D1S1") never match. Kept separate because
 *  canonicalizeDims needs the SKU width as its mm/cm ARBITER even when the
 *  dims string exists. */
export function skuWidthCm(u) {
  var p = String((u && u.sku) || "").split("-");
  for (var i = 0; i < p.length; i++) {
    var seg = p[i].match(/^(\d+(?:\.\d+)?)(?![\d.])/);
    if (seg) return parseFloat(seg[1]);
  }
  return null;
}

export function unitWidthCm(u) {
  // 2026-08-24 DIMS-FIRST (bridge parity — the "W10" bug): the dims string has
  // been the CANONICAL cm form since the 2026-08-09 exactness round, and the
  // configurator bridge (catalogBridge.unitDimsM, backend _unit_dims_m) already
  // reads it first — "a serial like TEST-001 must not read as a 1cm width".
  // The technician still parsed the SKU segment FIRST, so an ordinal SKU like
  // "BU-01-COPY" showed W=10mm here (degenerate bench preview, wrong per-width
  // prices) while the configurator showed the true 600 — the parsers MUST agree.
  var m = String((u && u.dims) || "").match(/(\d+(?:\.\d+)?)/);
  if (m) {
    var w = parseFloat(m[1]);
    return w >= 320 ? w / 10 : w;      // mm tolerance, same as cfgDimsM/bridge
  }
  return skuWidthCm(u);
}

export function unitConfig(u) { var p = String((u && u.sku) || "").split("-"); return p.length > 1 ? p[1] : ""; }
export function unitHasFront(u) { return unitConfig(u).toLowerCase().indexOf("op") !== 0; } // "Op*" configs are open (no front)
export function unitHasDrawers(u) { if (!u) return false; if (/drawer/i.test(u.cat || "")) return true; return /dr/i.test(unitConfig(u)); }
export function unitIsWall(u) { return /wall/i.test((u && u.cat) || "") || /^wu/i.test((u && u.sku) || ""); }
export function unitIsTall(u) { return /tall|column/i.test((u && u.cat) || "") || /^tu/i.test((u && u.sku) || ""); }
export function unitIsOpen(u) { return unitConfig(u).toLowerCase().indexOf("op") === 0; }
export function unitIsShelf(u) { return /shelf/i.test(((u && u.cat) || "") + " " + ((u && u.name) || "")) || unitIsOpen(u); }

/** unitFrontKeys(u) — the module's fronts in ENGINE SPEC ORDER (2026-08-11
 *  smart handles): explicit u.geometry wins, then the product NAME ("2
 *  drawers", "3 Doors") — the same inference the 3D bridge applies
 *  (catalogBridge.inferGeometryFromName), so "Drawer 2" here IS the second
 *  drawer the 3D builds. Drawers win over doors (engine rule). */
export function unitFrontKeys(u) {
  var g = (u && u.geometry) || {};
  var nm = String((u && u.name) || "");
  var drawers = parseInt(g.drawers, 10);
  var doors = parseInt(g.doors, 10);
  var m;
  if (!isFinite(drawers)) {
    m = nm.match(/(\d+)\s*(?:drawer|cassett|sirtar)/i);
    drawers = m ? parseInt(m[1], 10) : (/drawer|cassett|sirtar/i.test(nm) ? 1 : 0);
  }
  if (!isFinite(doors)) {
    m = nm.match(/(\d+)\s*(?:door|anta|ante|dyer|porta)/i);
    doors = m ? parseInt(m[1], 10) : 0;
  }
  var keys = [];
  var k;
  if (drawers > 0) {
    for (k = 0; k < Math.min(8, drawers); k++) keys.push({ key: "drawer-" + k, label: "Drawer " + (k + 1) });
  } else {
    var nDo = doors > 0 ? Math.min(6, doors) : (unitHasFront(u) ? 1 : 0);
    for (k = 0; k < nDo; k++) keys.push({ key: "door-" + k, label: "Door " + (k + 1) });
  }
  return keys;
}

// Which of the three lighting types apply to a unit.
export function lightingTypesFor(u) {
  var t = [];
  if (unitHasDrawers(u)) t.push("drawer");
  if (unitIsOpen(u) || unitIsTall(u) || unitIsWall(u)) t.push("vertical");
  if (unitIsWall(u) || unitIsShelf(u) || /base/i.test((u && u.cat) || "")) t.push("horizontal");
  return t;
}

export function unitFaceDims(u) {
  var m = String((u && u.dims) || "").match(/(\d+(?:\.\d+)?)\D+(\d+(?:\.\d+)?)/);
  if (!m) return null;
  function cm(x) { x = parseFloat(x); return x >= 320 ? x / 10 : x; }
  var w = cm(m[1]), h = cm(m[2]);
  if (u.doorW > 0) w = (u.doorW >= 320 ? u.doorW / 10 : u.doorW);
  if (u.doorH > 0) h = (u.doorH >= 320 ? u.doorH / 10 : u.doorH);
  return { w: w, h: h };
}

export function cfgDimsM(u) {
  var w = unitWidthCm(u), h = null, d = null;
  var m = (((u && u.dims) || "") + "").match(/(\d+(?:\.\d+)?)\D+(\d+(?:\.\d+)?)\D+(\d+(?:\.\d+)?)/);
  if (m) { if (w == null) w = parseFloat(m[1]); h = parseFloat(m[2]); d = parseFloat(m[3]); }
  if (!(h > 0)) { h = (u.cat === "Wall Unit") ? 72 : (u.cat === "Tall Unit" || u.cat === "Column") ? 210 : (u.cat === "Island") ? 90 : 78; }
  if (!(d > 0)) { d = (u.cat === "Wall Unit") ? 35 : (u.cat === "Island") ? 90 : 57; }
  function mm2cm(x) { return (x != null && x >= 320) ? x / 10 : x; } // tolerate mm input (e.g. 780 -> 78 cm)
  w = mm2cm(w); h = mm2cm(h); d = mm2cm(d);
  function r3(x) { return Math.round(x * 1000) / 1000; }
  return { width: r3((w || 0) / 100), depth: r3(d / 100), height: r3(h / 100) };
}

/* ── CANONICAL DIMS (2026-08-09 exactness round) ────────────────────────────
 * The dims string is parsed with a per-value mm tolerance (≥320 ⇒ mm ⇒ /10),
 * which CANNOT represent millimetre values under 320 — a wall unit imported
 * as "300x120x350" (mm) read as 300 CM wide in the configurator bridge while
 * the technician showed the SKU width. The canonical form is therefore CM
 * with decimals ("30×120×35", "60×78×57.5"): every axis < 320 by
 * construction, so BOTH parsers (catalog cfgDimsM and the JS/PY bridges)
 * read it identically, and width-from-dims equals width-from-SKU. */
function cmStr(cm) {
  const r = Math.round(cm * 100) / 100;
  return String(r);
}

/** dimsCanonicalFromMm(wMm,hMm,dMm) → "W×H×D" in cm (the unambiguous form). */
export function dimsCanonicalFromMm(wMm, hMm, dMm) {
  if (!(wMm > 0) || !(hMm > 0) || !(dMm > 0)) return null;
  return cmStr(wMm / 10) + "×" + cmStr(hMm / 10) + "×" + cmStr(dMm / 10);
}

/** dimsFromSizeMm(meta) → canonical dims string from the GLB's MEASURED size
 *  (model3dMeta.sizeMm {w,h,d}) — the uploaded model is the SketchUp truth. */
export function dimsFromSizeMm(meta) {
  const s = meta && meta.sizeMm;
  if (!s || !(s.w > 0) || !(s.h > 0) || !(s.d > 0)) return null;
  return dimsCanonicalFromMm(s.w, s.h, s.d);
}

/** canonicalizeDims(u) → the unit's CURRENT dims re-expressed canonically
 *  (technician interpretation preserved: SKU width wins when the dims width
 *  is its ×10 mm form; per-value mm tolerance elsewhere). null = leave alone
 *  (fewer than 3 numbers — never fabricate). */
export function canonicalizeDims(u) {
  const m = String((u && u.dims) || "").match(/(\d+(?:[.,]\d+)?)\D+(\d+(?:[.,]\d+)?)\D+(\d+(?:[.,]\d+)?)/);
  if (!m) return null;
  const num = (x) => parseFloat(String(x).replace(",", "."));
  let w = num(m[1]);
  const h = num(m[2]), d = num(m[3]);
  const mm2cm = (x) => (x >= 320 ? x / 10 : x);
  const skuW = skuWidthCm(u);   // the SKU segment is the arbiter here, never dims
  // "300" on a 30cm SKU is the mm form of the SAME width — read it as mm even
  // below the tolerance threshold (the WU-D1S2-30 configurator-3m bug).
  // 2026-08-24 GUARD: only a PLAUSIBLE module width may arbitrate (10..300cm).
  // An ORDINAL sku segment ("BU-01" → 1, "DIANA-2201-01-L" → 2201) is not a
  // width, and firing on it silently shrank real modules 10× and wrote it
  // back one-shot: "BU-01" + dims "10x78x57" → "1×78×57" (a 10cm filler
  // became 1cm). Implausible arbiters now fall through to the mm tolerance.
  const skuArb = (skuW != null && skuW >= 10 && skuW <= 300) ? skuW : null;
  if (skuArb != null && Math.abs(w - skuArb * 10) < 0.001) w = skuArb; else w = mm2cm(w);
  return cmStr(w) + "×" + cmStr(mm2cm(h)) + "×" + cmStr(mm2cm(d));
}

/** dimsMismatchMm(u) → {dims:{w,h,d}, glb:{w,h,d}, axes:[…]} in mm when the
 *  stored dims differ from the attached model's measured size by more than
 *  5mm on any axis; null when no model size or everything matches. */
export function dimsMismatchMm(u) {
  const s = u && u.model3dMeta && u.model3dMeta.sizeMm;
  if (!s || !(s.w > 0)) return null;
  const dm = cfgDimsM(u);
  const dims = { w: Math.round(dm.width * 1000), h: Math.round(dm.height * 1000), d: Math.round(dm.depth * 1000) };
  const glb = { w: Math.round(s.w), h: Math.round(s.h), d: Math.round(s.d) };
  const axes = ["w", "h", "d"].filter((k) => Math.abs(dims[k] - glb[k]) > 5);
  return axes.length ? { dims, glb, axes } : null;
}

export function unitHasWorktop(u) { return !!u && ["Base Unit", "Corner Base", "Drawer Unit", "Island"].indexOf(u.cat) >= 0; }
export function unitHasSink(u) { return !!u && /sink/i.test(u.name || ""); }

/* ── PIM classification (prototype 54694-54696) ─────────────────────────── */
export function unitGroup(u) {
  if (u && u.group) return u.group;
  var c = ((u && u.cat) || "").toLowerCase();
  if (/base|wall|tall|column|corner|drawer|sink|island|oven|appliance|kitchen/.test(c)) return "kitchens";
  return "kitchens";
}
export function unitSub(u) {
  // 2026-07-23 single-source-of-truth mandate: the technician's collections are
  // CANONICAL — a custom kitchen collection ("+ Add subcategory") is real, not
  // clamped back to the static five. Unassigned kitchen units default to
  // "Horizon" (the base collection) so legacy data keeps its home.
  if (unitGroup(u) === "kitchens") {
    return (u && u.sub) ? u.sub : "Horizon";
  }
  if (u && u.sub) return u.sub;
  return "";
}
export function unitType(u) { return (u && u.cat) ? u.cat : ""; }
